import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { makeStyles } from "tss-react/mui";

import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import FormGroup from "@mui/material/FormGroup";

import Switch from "../../../../../Common/Switch";
import Footer from "./Footer";

import { regexSnowflakeField } from "../../../../../../util/regularExpress";
import { checkReservedKeywords } from "../../../../../../util/restrictedStatement";
import { showToast } from "../../../../../../store/actions";

import { DATA_SOURCE } from "../../../../../../util/Data/DataModeling";
import DSContext from "../DSContext";

const designateList = ["None", "Primary", "Secondary"];
const derivedTypes = [
  {
    key: "constant",
    value: "Constant value",
  },
  {
    key: "current_date",
    value: "Current date",
  },
  {
    key: "filename",
    value: "Filename",
  },
  {
    key: "filename_no_ext",
    value: "Filename w/o extension",
  },
  {
    key: "parent_folder",
    value: "Parent folder",
  },
  {
    key: "partition_key",
    value: "Partition Key",
  },
];

const useStyles = makeStyles()((theme) => ({
  Switch: {
    "&.MuiSwitch-root": {
      marginLeft: 0,
    },
  },
}));

const Details = (
  {
    onClose,
    dataTableRef,
    setCurrentField,
    addField,
    currentField,
    newDatasource,
    IsDataVaultEnabled,
    containsIsDeleted,
  },
  ref
) => {
  const { fields, location, name: currentDatasource, datasetType } = useContext(DSContext);
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const currentFieldRef = useRef(currentField);

  const attributeDataTypes = useSelector((state) => state.config.attributeDataTypes);
  const dataSourceData = useSelector((state) => state.dataSourceData);

  const { dataSources } = dataSourceData;

  const types = useMemo(
    () => [
      {
        value: "NoOption",
        text: "Select Type",
      },
      ...attributeDataTypes.map((option) => ({ value: option.type, text: option.type, options: option.options })),
    ],
    [attributeDataTypes]
  );

  // State Data for Detail Tab
  const [attribute, setAttribute] = useState("");
  const [attributeError, setAttributeError] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState("NoOption");
  const [length, setLength] = useState("NoOption");
  const [oldLength, setOldLength] = useState("NoOption");
  const [openLengthList, setOpenLengthList] = useState(false);
  const [openFormatList, setOpenFormatList] = useState(false);
  const [format, setFormat] = useState(null);
  const [customFormat, setCustomFormat] = useState("");
  const [refDataSource, setRefDataSource] = useState("NoOption");
  const [refDSField, setRefDSField] = useState("NoOption");
  const [designateField, setDesignateField] = useState("None");
  const [enableDesignate, setEnableDesignate] = useState(false);
  const [dataVaultKey, setDataVaultKey] = useState(false);
  const [isDerived, setIsDerived] = useState(false);
  const [derivedBy, setDerivedBy] = useState("");
  const [derivedValue, setDerivedValue] = useState("");
  const [isDeleted, setIsDeleted] = useState(false);
  const [isDeletedRegex, setIsDeletedRegex] = useState("");
  const [dqm, setDqm] = useState("");

  const openDSList = useMemo(() => type === "Reference", [type]);
  const customFormatOpen = useMemo(() => format === "Custom", [format]);

  const enableDetailsSave = () =>
    !(attribute && type && type !== "NoOption") ||
    (openLengthList && length === "NoOption") ||
    (openDSList && refDataSource === "NoOption") ||
    (openDSList && refDSField === "NoOption") ||
    (openFormatList && format === "NoOption") ||
    (customFormatOpen && customFormat === "") ||
    (isDeleted && isDeletedRegex.trim() === "") ||
    (isDerived && (derivedBy === "" || (["partition_key", "constant"].includes(derivedBy) && derivedValue === "")));

  useImperativeHandle(ref, () => ({ enableDetailsSave, handleSaveDetails }));

  const [lengthList, setLengthList] = useState([]);
  const [formatList, setFormatList] = useState([]);
  const [dsList, setDsList] = useState([]);
  const [dsFieldList, setDsFieldList] = useState([{ value: "NoOption", text: `Select ${DATA_SOURCE.singular} Field` }]);
  const [currentFieldRefTo, setCurrentFieldRefTo] = useState([]);

  const $dataSources = useMemo(() => {
    const tDatasources = JSON.parse(JSON.stringify(dataSources));
    if (newDatasource || !dataSources.some(({ name }) => name === currentDatasource))
      tDatasources.unshift({
        name: currentDatasource,
        fields,
      });

    return tDatasources;
  }, [currentDatasource, dataSources, fields, newDatasource]);

  const refToType = useMemo(() => {
    if (type !== "Reference") return "";

    const _field = dsFieldList.find(({ value }) => value === refDSField);
    if (!_field || _field.value === "NoOption") return "";

    return _field.info.type === "Reference" ? _field.info.referenceFrom.field.type : _field.info.type;
  }, [dsFieldList, refDSField, type]);

  const handleAttribute = (str, setData) => {
    const isValid = str.length !== 0 ? regexSnowflakeField(str) : true;
    if (isValid) setData(str);
  };

  //Get Length Options from selected values of "Type" Dropdown
  const getLengthOptions = useCallback(
    (selectedDataType, selectedLength) => {
      setLength("NoOption");
      setOpenLengthList(false);

      if (types.length > 0) {
        const lengthArr = [{ value: "NoOption", text: "Select Length" }];

        types.forEach((row) => {
          if (selectedDataType === row.value && row.options && row.options.length > 0) {
            row.options.forEach((option) => {
              lengthArr.push({ value: option.value, text: option.value });

              if (currentField && selectedLength && option.value === selectedLength) {
                setLength(selectedLength);
                setOldLength(selectedLength);
              }
            });

            setOpenLengthList(true);
          }
        });

        setLengthList(lengthArr);
      }
    },
    [currentField, types]
  );

  //Get Format Options from selected values of "Type" Dropdown
  const getFormatOptions = useCallback(
    (selectedDataType, selectedFormat = null, customFormat = "") => {
      if (format === null || format === undefined) {
        if (selectedDataType === "Date") setFormat("YYYY-MM-DD");
        else if (selectedDataType === "DateTime") setFormat("YYYY-MM-DD HH:MI:SS");
        else if (selectedDataType === "Time") setFormat("HH:MI:SS");
      }
      setLength("NoOption");
      setOpenFormatList(false);
      setCustomFormat("");

      if (types.length > 0) {
        let formatArr = [];

        types.forEach((row) => {
          if (selectedDataType === row.value && row.options && row.options.length > 0) {
            row.options.forEach((option, i) => {
              formatArr.push({ value: option.value, text: option.value });

              if (currentField && selectedFormat && option.value === selectedFormat) {
                setFormat(selectedFormat);
                setCustomFormat(selectedFormat === "Custom" ? customFormat : "");
              }
            });

            setOpenFormatList(true);
          }
        });

        setFormatList(formatArr);
      }
    },
    //don't add format here, its creating rendering issue
    [currentField, types]
  );

  // Fetching current field all details from feilds, if we want to use it
  const $currentField = useMemo(() => {
    if (currentField) {
      const tField = fields.find((field) => field.name === currentField);
      return tField;
    } else return {};
  }, [fields, currentField]);

  const [previousType, previousLength, previousFormat] = useMemo(() => {
    if (!$currentField) return ["", "", { format: "", customFormat: "" }];

    if ($currentField.type !== "Reference")
      return [
        $currentField.type,
        $currentField.length,
        { format: $currentField.format, customFormat: $currentField.customFormat },
      ];

    return [
      $currentField.referenceFrom.field.type,
      $currentField.referenceFrom.field.length,
      {
        format: $currentField.referenceFrom.field.format,
        customFormat: $currentField.referenceFrom.field.customFormat,
      },
    ];
  }, [$currentField]);

  //Get DataSet's Field List for "DataSet Field" Dropdown for reference mapping
  const getDSFieldList = useCallback(
    async (selectedDS = "") => {
      let dsFieldArr = [{ value: "NoOption", text: `Select ${DATA_SOURCE.singular} Field` }];

      if (selectedDS && Object.keys($dataSources).length > 0) {
        const selected = $dataSources.find((ds) => ds.name === selectedDS);

        const dsFields =
          (!selected && newDatasource) || selectedDS === currentDatasource
            ? JSON.parse(JSON.stringify(fields))
            : JSON.parse(JSON.stringify(selected?.fields || "[]")) || [];

        if (dsFields.length) {
          dsFields.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
          dsFields.forEach((field) => {
            //Not allowig to select current field
            if (selectedDS === currentDatasource && field.name === currentField) return;

            if (field.dataVaultKey) {
              const $type = field.type === "Reference" ? field.referenceFrom.field.type : field.type;
              const $length = field.type === "Reference" ? field.referenceFrom.field.length : field.length;

              let isValid = true;
              if ($type === "String" || $type === "Char") isValid = $length >= previousLength;
              else if ($type === "Number") isValid = $length === previousLength;

              if (!currentField || (currentField && $type === previousType && isValid))
                dsFieldArr.push({ value: field.name, text: field.name, info: field });
            }
          });
        }
      }
      setDsFieldList(dsFieldArr);
    },
    [currentDatasource, currentField, $dataSources, fields, newDatasource, previousLength, previousType]
  );

  //Get DataSet List for "DataSet" Dropdown for reference
  const getDSList = useCallback(() => {
    const tDatasources = $dataSources;

    const refToSources = currentFieldRefTo.map(({ data_source }) => data_source);

    const dataSourceArr = [{ value: "NoOption", text: `Select ${DATA_SOURCE.singular}` }];
    if (Object.keys(tDatasources).length > 0) {
      tDatasources.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
      tDatasources.forEach((option) => {
        if (!refToSources.includes(option.name)) dataSourceArr.push({ value: option.name, text: option.name });
      });
    }
    setDsList(dataSourceArr);
    if (!currentField) getDSFieldList();
  }, [$dataSources, currentFieldRefTo, currentField, getDSFieldList]);

  useEffect(() => {
    const tField = fields.find((field) => field.name === currentField);
    if (tField) {
      const designateSelected = tField.primary_attribute
        ? "Primary"
        : tField.secondary_attribute
        ? "Secondary"
        : "None";

      setAttribute(tField.name);
      types.forEach((option) => {
        if (currentField && tField.type && option.value === tField.type) setType(tField.type);
      });
      setDescription(tField.description);
      setDesignateField(designateSelected);
      setDataVaultKey(!!tField.dataVaultKey);
      setEnableDesignate(tField.entity_mapping && Object.keys(tField.entity_mapping).length);
      setIsDerived(!!tField.isDerived);
      setDerivedBy(tField.derivedBy || "");
      setDerivedValue(tField.derivedValue || "");
      setIsDeleted((tField?.isDeleted && tField?.isDeleted?.length > 0) || false);
      setIsDeletedRegex(tField?.isDeleted || "");
      setDqm(tField?.dqm?.required || false);

      //Get and Set CurrentField's ReferenceTo object (by matching from all fields) to enabled/disabled primary key dropdown
      setCurrentFieldRefTo(tField.referenceTo && tField.referenceTo.length > 0 ? tField.referenceTo : []);

      if (tField.type === "Reference") {
        if (tField?.referenceFrom?.data_source && tField.referenceFrom?.field?.name) {
          getDSFieldList(tField.referenceFrom.data_source);
          setRefDataSource(tField.referenceFrom.data_source);
          setRefDSField(tField.referenceFrom.field.name);
          setFormat(tField.referenceFrom.field.format || "");
          setCustomFormat(tField.referenceFrom.field.customFormat || "");
        }
      }
    }
  }, [currentField, fields, getDSFieldList, types]);

  useEffect(() => {
    if (currentField) {
      const tField = fields.find((field) => field.name === currentField);
      if (tField) {
        if (tField.type === "Date" || tField.type === "DateTime" || tField.type === "Time")
          getFormatOptions(tField.type, tField.format, tField.customFormat);
        else getLengthOptions(tField.type, tField.length);
      }
    }
    getDSList();
  }, [currentField, fields, getLengthOptions, getDSList, getFormatOptions]);

  const $types = useMemo(() => {
    if (IsDataVaultEnabled) return types;
    return types.filter(({ value }) => value.toLowerCase() !== "reference");
  }, [IsDataVaultEnabled, types]);

  const validateAttributeName = () => {
    if (checkReservedKeywords(attribute)) return "Reserved keyword.";
    else if (
      fields.some(
        (field) =>
          field.name.toLowerCase() === attribute.toLowerCase() && currentField.toLowerCase() !== attribute.toLowerCase()
      )
    )
      return "Name must be Unique.";
    else if (attribute.length > 128) return "Name must be less than 128 characters.";
    return "";
  };

  const handleSaveDetails = (saveBy) => {
    const errorMessage = validateAttributeName();
    if (errorMessage) setAttributeError(errorMessage);
    else {
      //Fetch the fields which have same designate as current field have selected
      let restFieldsArr = fields.filter(
        (field) =>
          field.name.toLowerCase() !== currentField.toLowerCase() &&
          (field.primary_attribute || field.secondary_attribute)
      );

      //Remove designate from the old fields which have same designate as current field have selected
      if (restFieldsArr.length) {
        restFieldsArr.forEach((restField) => {
          if (designateField === "Primary") restField.primary_attribute = false;
          if (designateField === "Secondary") restField.secondary_attribute = false;

          dataTableRef.current.updateRow(restField.name, restField);
        });
      }

      //Add designate to current field
      const tField = fields.find((field) => field.name === currentField);

      //Logic to add Reference field
      let referenceFrom = {};
      if (refDataSource && refDataSource !== "NoOption" && refDSField && Object.keys($dataSources).length > 0) {
        //Logic to get refered DataSet from dataset list
        const dsObj = $dataSources.find((ds) => ds.name === refDataSource);

        if (!dsObj && !newDatasource) return;

        const $fields =
          (!dsObj && newDatasource) || refDataSource === currentDatasource
            ? JSON.parse(JSON.stringify(fields))
            : JSON.parse(JSON.stringify(dsObj.fields)) || [];

        if ($fields.length) {
          //Logic to get refered field from fetch DataSet's field list
          const dsFieldObj = $fields.find((field) => field.name === refDSField);

          if (dsFieldObj && Object.keys(dsFieldObj).length) {
            dsFieldObj.referenceTo = dsFieldObj.referenceTo || [];

            //Checking if its already there in reference or not
            // Adding if not found
            if (
              !dsFieldObj.referenceTo.some(
                (reff) => reff.data_source === currentDatasource && reff.field.name === currentFieldRef.current
              ) &&
              currentDatasource.trim() !== ""
            ) {
              //Logic to create JSON object of referenceTo(Current(as child) DataSet & its Field) for Parent DataSet
              const toFieldInfo = {
                name: attribute,
                type: dsFieldObj.type === "Reference" ? dsFieldObj.referenceFrom.field.type : dsFieldObj.type,
                length: dsFieldObj.type === "Reference" ? dsFieldObj.referenceFrom.field.length : dsFieldObj.length,
                format: dsFieldObj.type === "Reference" ? dsFieldObj.referenceFrom.field.format : dsFieldObj.format,
                customFormat:
                  dsFieldObj.type === "Reference"
                    ? dsFieldObj.referenceFrom.field.customFormat
                    : dsFieldObj.customFormat,
              };

              dsFieldObj.referenceTo.push({ data_source: currentDatasource, field: toFieldInfo });
            }

            //Logic to create JSON object of referenceFrom(Parent DataSet & its Field) for Current DataSet
            const fromFieldInfo = {
              name: dsFieldObj.name,
              type: dsFieldObj.type === "Reference" ? dsFieldObj.referenceFrom.field.type : dsFieldObj.type,
              length: dsFieldObj.length,
              format: dsFieldObj.format !== null ? dsFieldObj.format : "",
              customFormat:
                dsFieldObj.format === "Custom" && dsFieldObj.customFormat !== null ? dsFieldObj.customFormat : "",
            };
            referenceFrom = { data_source: refDataSource, field: fromFieldInfo };
          }
        }
      }
      addField({
        ...tField,
        name: attribute,
        type: type !== "NoOption" ? type : "",
        description,
        length: length !== "NoOption" ? length : "",
        format: format !== null ? format : "",
        customFormat: format === "Custom" && customFormat !== null ? customFormat : "",
        primary_attribute: designateField === "Primary",
        secondary_attribute: designateField === "Secondary",
        referenceFrom: referenceFrom,
        dataVaultKey: dataVaultKey,
        isDerived,
        derivedBy,
        derivedValue,
        isDeleted: isDeleted ? isDeletedRegex.trim() : "",
      });

      //Update length into the OldLength var to disbale length less then previouly selected
      setOldLength(length);

      //Update the current Field when rename the attribute
      setCurrentField(attribute);
    }

    if (errorMessage === "") {
      if (saveBy !== "auto") {
        dispatch(
          showToast({
            message: `Attribute ${currentField ? "saved" : "added"} successfully.`,
            type: "success",
          })
        );
        onClose();
      }
    }
  };

  return (
    <>
      <Box sx={{ width: "100%", height: "auto", marginTop: "20px" }} role="presentation">
        <TextField
          required
          autoFocus
          error={!!attributeError}
          helperText={attributeError}
          value={attribute}
          inputProps={{ maxLength: 128 }}
          onBlur={(e) => setAttributeError(validateAttributeName())}
          onChange={(e) => handleAttribute(e.target.value, setAttribute)}
          fullWidth
          label="Attribute"
          variant="outlined"
          sx={{ my: 2 }}
          disabled={datasetType === "readonly"}
        />
        <FormControl required fullWidth variant="outlined" sx={{ my: 2 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              if (e.target.value === "Reference") {
                setOpenLengthList(false);
                setOpenFormatList(false);
              } else if (e.target.value === "Date" || e.target.value === "DateTime" || e.target.value === "Time") {
                setRefDataSource("NoOption");
                setRefDSField("NoOption");
                getFormatOptions(e.target.value, previousFormat.format, previousFormat.customFormat);
                setOpenLengthList(false);
              } else {
                getLengthOptions(e.target.value, previousLength);
                setRefDataSource("NoOption");
                setRefDSField("NoOption");
                setOpenFormatList(false);
              }
            }}
            label="Type"
            disabled={datasetType === "readonly"}
          >
            {$types.map((_type) => (
              <MenuItem
                key={_type.value}
                value={_type.value}
                disabled={Boolean(
                  _type.value === "NoOption" ||
                    (currentField && _type.value !== "Reference" && _type.value !== (refToType || previousType || type))
                )}
              >
                {_type.text}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {openLengthList && (
          <FormControl required fullWidth variant="outlined" sx={{ my: 2 }}>
            <InputLabel>Length</InputLabel>
            <Select
              value={length}
              onChange={(e) => setLength(e.target.value)}
              label="Length"
              disabled={!(newDatasource || (!newDatasource && !currentField)) && type !== "String" && type !== "Char"}
            >
              {lengthList.map((type) => (
                <MenuItem
                  key={type.value}
                  value={type.value}
                  disabled={
                    type.value === "NoOption" ||
                    (type.value !== "NoOption" && oldLength !== "NoOption" && type.value < oldLength)
                  }
                >
                  {type.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {openDSList && IsDataVaultEnabled && (
          <FormControl required fullWidth variant="outlined" sx={{ my: 2 }}>
            <InputLabel>{`${DATA_SOURCE.singular}`}</InputLabel>
            <Select
              value={dsList.length > 1 && refDataSource ? refDataSource : "NoOption"}
              onChange={(e) => {
                setRefDSField("NoOption");
                setRefDataSource(e.target.value);
                getDSFieldList(e.target.value);
              }}
              label={`${DATA_SOURCE.singular}`}
            >
              {dsList.map((option) => (
                <MenuItem key={option.value} value={option.value} disabled={option.value === "NoOption"}>
                  {option.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {openDSList && IsDataVaultEnabled && (
          <FormControl required fullWidth variant="outlined" sx={{ my: 2 }}>
            <InputLabel>{`${DATA_SOURCE.singular} Field`}</InputLabel>
            <Select
              value={dsFieldList.length > 1 && refDSField ? refDSField : "NoOption"}
              onChange={(e) => setRefDSField(e.target.value)}
              label={`${DATA_SOURCE.singular} Field`}
            >
              {dsFieldList.map((option) => (
                <MenuItem key={option.value} value={option.value} disabled={option.value === "NoOption"}>
                  {option.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {openFormatList && (
          <FormControl required fullWidth variant="outlined" sx={{ my: 2 }}>
            <InputLabel>Format</InputLabel>
            <Select
              value={format !== null ? format : formatList[0]?.value}
              onChange={(e) => {
                if (e.target.value !== "Custom") setCustomFormat("");
                setFormat(e.target.value);
              }}
              label="Format"
              disabled={location === "SNOWFLAKE"}
            >
              {formatList.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {openFormatList && customFormatOpen && (
          <TextField
            value={customFormat}
            inputProps={{ maxLength: 30 }}
            onChange={(e) => setCustomFormat(e.target.value)}
            fullWidth
            label="Custom Format"
            variant="outlined"
            sx={{ my: 2 }}
            required
            disabled={location === "SNOWFLAKE"}
          />
        )}

        {location === "SNOWFLAKE" && (type === "Date" || type === "DateTime" || type === "Time") && (
          <Typography variant="body2">
            Data types and its attributes will be taken straight from the table which you have selected as your source
          </Typography>
        )}
        <TextField
          value={description}
          inputProps={{ maxLength: 120 }}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          label="Description"
          variant="outlined"
          sx={{ my: 2 }}
        />

        {enableDesignate && !IsDataVaultEnabled ? (
          <FormControl fullWidth variant="outlined" sx={{ margin: "10px 0 20px" }}>
            <InputLabel>Designate Primary/Secondary</InputLabel>
            <Select
              value={designateField}
              onChange={(e) => setDesignateField(e.target.value)}
              label="Designate Primary/Secondary"
            >
              {designateList.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          ""
        )}
        {IsDataVaultEnabled && (
          <FormControlLabel
            control={
              <Checkbox
                checked={dataVaultKey}
                onChange={() => setDataVaultKey((prev) => !prev)}
                disabled={currentFieldRefTo.length > 0 || isDeleted}
              />
            }
            label="Data Vault Key"
          />
        )}
        <FormGroup aria-label="position" row>
          <FormControlLabel
            value={isDerived}
            control={<Switch className={classes.Switch} />}
            label="Derived Value"
            labelPlacement="end"
            sx={{ margin: "0" }}
            onChange={(e) => {
              setIsDerived((prev) => {
                if (!prev) setDerivedBy("partition_key");
                return !prev;
              });
            }}
            checked={isDerived}
            disabled={isDeleted || datasetType === "readonly"}
          />
        </FormGroup>

        {isDerived && (
          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid size={6}>
              <FormControl required fullWidth variant="outlined" disabled={isDeleted || datasetType === "readonly"}>
                <InputLabel>Derived by</InputLabel>
                <Select
                  value={derivedBy}
                  onChange={(e) => {
                    setDerivedBy(e.target.value);
                    setDerivedValue("");
                  }}
                  label="Derived by"
                >
                  {derivedTypes.map((opt) => (
                    <MenuItem key={opt.key} value={opt.key}>
                      {opt.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {["partition_key", "constant"].includes(derivedBy) && (
              <Grid size={6}>
                <TextField
                  value={derivedValue}
                  onChange={(e) => setDerivedValue(e.target.value)}
                  fullWidth
                  label="Value"
                  variant="outlined"
                />
              </Grid>
            )}
          </Grid>
        )}
        {IsDataVaultEnabled && (
          <>
            <FormGroup aria-label="position" row>
              <FormControlLabel
                value={isDeleted}
                control={<Switch className={classes.Switch} />}
                label="Deletion Flag"
                labelPlacement="end"
                sx={{ margin: "0" }}
                onChange={(e) => {
                  setIsDeleted((prev) => {
                    if (!prev && isDeletedRegex === "") {
                      setIsDeletedRegex("^(Yes|Y|1)$");
                    }
                    return !prev;
                  });
                }}
                checked={isDeleted}
                disabled={
                  dataVaultKey ||
                  isDerived ||
                  dqm ||
                  ($currentField?.entity_mapping &&
                    Object.keys($currentField.entity_mapping).length &&
                    $currentField.entity_mapping.hasOwnProperty("entity") &&
                    $currentField.entity_mapping.hasOwnProperty("field")) ||
                  ($currentField.hasOwnProperty("isDeleted") && $currentField.isDeleted !== ""
                    ? false
                    : containsIsDeleted) ||
                  datasetType === "readonly"
                }
              />
            </FormGroup>
            {isDeleted ? (
              <TextField
                value={isDeletedRegex}
                inputProps={{ maxLength: 300 }}
                onChange={(e) => setIsDeletedRegex(e.target.value)}
                fullWidth
                label="Custom Regex"
                variant="outlined"
                sx={{ my: 2 }}
                disabled={dataVaultKey || !isDeleted || isDerived}
                required
              />
            ) : (
              <></>
            )}
          </>
        )}
      </Box>
      <Footer disabled={enableDetailsSave()} handleSaveDetails={handleSaveDetails} onClose={onClose} />
    </>
  );
};

export default forwardRef(Details);
